import React, { useState, useEffect, useRef, useCallback } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './SimpleDrawingBoard.css';
import io from 'socket.io-client';

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 800;

const SimpleDrawingBoard = ({ onBack }) => {
  // Función helper para obtener la URL del API
  const getApiUrl = () => {
    return window.location.hostname === 'localhost' 
      ? 'http://localhost:5000/api'  // En desarrollo local
      : 'https://felizcumplecamipreciosa.lat/api';  // En producción
  };

  const getSocketUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:5000';  // En desarrollo local
    } else {
      return {
        url: 'https://felizcumplecamipreciosa.lat',
        path: '/api/socket.io'
      };
    }
  };

  // Estados básicos
  const [currentTool, setCurrentTool] = useState('brush');
  const [currentColor, setCurrentColor] = useState('#8a2be2');
  const [brushSize, setBrushSize] = useState(5);
  const [brushType, setBrushType] = useState('normal'); // normal, spray, blur, graffiti, texture
  const [opacity, setOpacity] = useState(100);
  const [transparency, setTransparency] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [points, setPoints] = useState([]);
  
  // Estados para herramienta de selección
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  
  // Estados para zoom, pan y rotación
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [isMiddleMousePanning, setIsMiddleMousePanning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Estados para formas
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [shapeStart, setShapeStart] = useState(null);
  
  // Historial
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null); // Canvas para previsualización de formas
  const containerRef = useRef(null);
  const socketRef = useRef(null);

  // Detectar dispositivo móvil
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Guardar automáticamente cuando el usuario sale de la página
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDrawingToServer();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Guardar cada 10 segundos en lugar de cada 30 segundos
    const autoSaveInterval = setInterval(() => {
      saveDrawingToServer();
    }, 10000);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(autoSaveInterval);
    };
  }, []);

  // API functions for saving/loading drawings from server
  const loadDrawingFromServer = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/drawing`);
      if (!response.ok) {
        if (response.status === 500) {
          console.warn('Backend error al cargar dibujo, usando canvas vacío');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      if (data.canvas_data) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          saveToHistory();
        };
        img.src = data.canvas_data;
      }
    } catch (error) {
      console.error('Error loading drawing:', error);
    }
  };

  const saveDrawingToServer = async () => {
    try {
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL();
      
      const response = await fetch(`${getApiUrl()}/drawing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ canvas_data: dataURL }),
      });
      
      if (!response.ok) {
        if (response.status === 500) {
          console.warn('Backend error al guardar, continuando sin guardar');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving drawing:', error);
      // No mostrar error al usuario, solo loggear
    }
  };

  // Inicializar canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      // Cargar dibujo desde el servidor
      loadDrawingFromServer();
    }
    
    // Inicializar canvas de previsualización
    const previewCanvas = previewCanvasRef.current;
    if (previewCanvas) {
      const ctx = previewCanvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  // Función para obtener coordenadas del canvas
  const getCanvasCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    // Ajustar por transformaciones
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    let x = (clientX - centerX) / zoom;
    let y = (clientY - centerY) / zoom;
    
    // Ajustar por rotación
    if (rotation !== 0) {
      const angle = -rotation * Math.PI / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const newX = x * cos - y * sin;
      const newY = x * sin + y * cos;
      x = newX;
      y = newY;
    }
    
    // Ajustar por pan
    x -= pan.x / zoom;
    y -= pan.y / zoom;
    
    // NO redondear - mantener precisión decimal para resolución vectorial
    return {
      x: x + CANVAS_WIDTH / 2,
      y: y + CANVAS_HEIGHT / 2
    };
  }, [zoom, pan, rotation]);

  // Guardar en historial
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dataURL = canvas.toDataURL();
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(dataURL);
    
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryStep(prev => prev + 1);
    }
    
    setHistory(newHistory);
    
    // Solo guardar en servidor cuando se completa una acción
    // No en cada trazo para evitar saturar
  }, [history, historyStep]);

  // Dibujar en el canvas
  // Función para recortar área seleccionada
  const cutSelectedArea = useCallback(() => {
    if (!selectedArea) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Capturar la imagen del área seleccionada
    const imageData = ctx.getImageData(
      selectedArea.x, 
      selectedArea.y, 
      selectedArea.width, 
      selectedArea.height
    );
    
    setSelectedImageData(imageData);
    
    // Limpiar el área del canvas original (recortar)
    ctx.clearRect(selectedArea.x, selectedArea.y, selectedArea.width, selectedArea.height);
    
    // Emitir evento de corte a otros usuarios
    if (socketRef.current) {
      socketRef.current.emit('cut-selection', {
        x: selectedArea.x,
        y: selectedArea.y,
        width: selectedArea.width,
        height: selectedArea.height
      });
    }
    
    saveToHistory();
  }, [selectedArea, saveToHistory]);

  // Función para detectar qué handle de redimensionamiento se está tocando
  const getResizeHandle = useCallback((pos) => {
    if (!selectedArea) return null;
    
    const handleSize = 8;
    const tolerance = 5;
    
    // Esquina superior izquierda
    if (Math.abs(pos.x - selectedArea.x) <= handleSize + tolerance && 
        Math.abs(pos.y - selectedArea.y) <= handleSize + tolerance) {
      return 'top-left';
    }
    // Esquina superior derecha
    if (Math.abs(pos.x - (selectedArea.x + selectedArea.width)) <= handleSize + tolerance && 
        Math.abs(pos.y - selectedArea.y) <= handleSize + tolerance) {
      return 'top-right';
    }
    // Esquina inferior izquierda
    if (Math.abs(pos.x - selectedArea.x) <= handleSize + tolerance && 
        Math.abs(pos.y - (selectedArea.y + selectedArea.height)) <= handleSize + tolerance) {
      return 'bottom-left';
    }
    // Esquina inferior derecha
    if (Math.abs(pos.x - (selectedArea.x + selectedArea.width)) <= handleSize + tolerance && 
        Math.abs(pos.y - (selectedArea.y + selectedArea.height)) <= handleSize + tolerance) {
      return 'bottom-right';
    }
    
    return null;
  }, [selectedArea]);

  // Función para aplicar la selección al canvas
  const applySelection = useCallback(() => {
    if (!selectedImageData || !selectedArea) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Crear un canvas temporal para redimensionar si es necesario
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = selectedImageData.width;
    tempCanvas.height = selectedImageData.height;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.putImageData(selectedImageData, 0, 0);
    
    // Dibujar la imagen redimensionada en la nueva posición
    ctx.drawImage(
      tempCanvas, 
      0, 0, selectedImageData.width, selectedImageData.height,
      selectedArea.x, selectedArea.y, selectedArea.width, selectedArea.height
    );
    
    // Emitir evento de aplicar selección a otros usuarios
    if (socketRef.current) {
      // Convertir imageData a formato más eficiente
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = selectedImageData.width;
      tempCanvas.height = selectedImageData.height;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.putImageData(selectedImageData, 0, 0);
      
      socketRef.current.emit('apply-selection', {
        imageDataURL: tempCanvas.toDataURL(),
        originalWidth: selectedImageData.width,
        originalHeight: selectedImageData.height,
        area: selectedArea
      });
    }
    
    // Limpiar selección
    setSelectedArea(null);
    setSelectedImageData(null);
    saveToHistory();
  }, [selectedImageData, selectedArea, saveToHistory]);

  const draw = useCallback((startPos, endPos) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.save();
    
    // Configuración básica para trazos perfectos
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.globalCompositeOperation = currentTool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.globalAlpha = (opacity - transparency) / 100;
    
    // Calcular distancia para interpolación en movimientos rápidos
    const distance = Math.sqrt(
      Math.pow(endPos.x - startPos.x, 2) + 
      Math.pow(endPos.y - startPos.y, 2)
    );
    
    if (brushType === 'spray') {
      // Efecto spray/aerosol
      drawSpray(ctx, endPos, brushSize, currentColor);
    } else if (brushType === 'blur') {
      // Efecto difuminado
      ctx.filter = `blur(${Math.max(1, brushSize / 5)}px)`;
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = brushSize * 1.5;
      ctx.beginPath();
      ctx.moveTo(startPos.x, startPos.y);
      ctx.lineTo(endPos.x, endPos.y);
      ctx.stroke();
      ctx.filter = 'none';
    } else if (brushType === 'graffiti') {
      // Efecto grafiti con textura irregular
      drawGraffiti(ctx, startPos, endPos, brushSize, currentColor);
    } else if (brushType === 'texture') {
      // Pincel con textura
      drawTextured(ctx, startPos, endPos, brushSize, currentColor);
    } else {
      // Pincel normal - con interpolación para movimientos rápidos
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = brushSize;
      
      // Si la distancia es grande (movimiento rápido), interpolar puntos
      if (distance > brushSize * 2) {
        const steps = Math.ceil(distance / (brushSize / 2));
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const x = startPos.x + (endPos.x - startPos.x) * t;
          const y = startPos.y + (endPos.y - startPos.y) * t;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      } else {
        // Para distancias cortas, línea directa
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(endPos.x, endPos.y);
        ctx.stroke();
      }
    }
    
    ctx.restore();
    
    // Emitir trazo a otros usuarios
    if (socketRef.current) {
      socketRef.current.emit('drawing', {
        lastPos: startPos,
        currentPos: endPos,
        tool: currentTool,
        color: currentColor,
        size: brushSize,
        opacity: opacity / 100,
        brushType: brushType
      });
    }
  }, [currentTool, currentColor, brushSize, brushType, opacity]);

  // Función para efecto spray
  const drawSpray = (ctx, pos, size, color) => {
    const density = Math.max(5, size);
    ctx.fillStyle = color;
    
    for (let i = 0; i < density; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * size;
      const x = pos.x + Math.cos(angle) * radius;
      const y = pos.y + Math.sin(angle) * radius;
      
      ctx.beginPath();
      ctx.arc(x, y, Math.random() * 2 + 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  // Función para efecto grafiti
  const drawGraffiti = (ctx, startPos, endPos, size, color) => {
    const steps = Math.max(5, Math.sqrt(Math.pow(endPos.x - startPos.x, 2) + Math.pow(endPos.y - startPos.y, 2)));
    ctx.strokeStyle = color;
    
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = startPos.x + (endPos.x - startPos.x) * t;
      const y = startPos.y + (endPos.y - startPos.y) * t;
      
      // Añadir variación irregular
      const offsetX = (Math.random() - 0.5) * size * 0.3;
      const offsetY = (Math.random() - 0.5) * size * 0.3;
      const currentSize = size + (Math.random() - 0.5) * size * 0.4;
      
      ctx.lineWidth = Math.max(1, currentSize);
      ctx.beginPath();
      ctx.arc(x + offsetX, y + offsetY, currentSize / 4, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  // Función para efecto texturizado
  const drawTextured = (ctx, startPos, endPos, size, color) => {
    ctx.fillStyle = color;  // Usar fillStyle en lugar de strokeStyle
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    
    // Crear patrón de puntos para textura
    const distance = Math.sqrt(Math.pow(endPos.x - startPos.x, 2) + Math.pow(endPos.y - startPos.y, 2));
    const steps = Math.max(3, distance / 2);
    
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = startPos.x + (endPos.x - startPos.x) * t;
      const y = startPos.y + (endPos.y - startPos.y) * t;
      
      // Dibujar varios círculos pequeños para crear textura
      for (let j = 0; j < 3; j++) {
        const offsetX = (Math.random() - 0.5) * size * 0.5;
        const offsetY = (Math.random() - 0.5) * size * 0.5;
        const dotSize = Math.random() * size * 0.3 + 1;
        
        ctx.beginPath();
        ctx.arc(x + offsetX, y + offsetY, dotSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  };

  // Función para aplicar bucket fill de otros usuarios
  const applyRemoteBucketFill = useCallback((data) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const imageDataArray = imageData.data;
    
    const startPos = (Math.floor(data.y) * canvas.width + Math.floor(data.x)) * 4;
    const startR = imageDataArray[startPos];
    const startG = imageDataArray[startPos + 1];
    const startB = imageDataArray[startPos + 2];
    const startA = imageDataArray[startPos + 3];
    
    const fillColor = {
      r: parseInt(data.color.slice(1, 3), 16),
      g: parseInt(data.color.slice(3, 5), 16),
      b: parseInt(data.color.slice(5, 7), 16),
      a: Math.floor(255 * data.opacity / 100)
    };
    
    if (startR === fillColor.r && startG === fillColor.g && startB === fillColor.b) return;
    
    const pixelsToCheck = [{ x: Math.floor(data.x), y: Math.floor(data.y) }];
    
    while (pixelsToCheck.length > 0) {
      const { x, y } = pixelsToCheck.pop();
      
      if (x < 0 || x >= canvas.width || y < 0 || y >= canvas.height) continue;
      
      const pos = (y * canvas.width + x) * 4;
      
      if (imageDataArray[pos] === startR && imageDataArray[pos + 1] === startG && 
          imageDataArray[pos + 2] === startB && imageDataArray[pos + 3] === startA) {
        
        imageDataArray[pos] = fillColor.r;
        imageDataArray[pos + 1] = fillColor.g;
        imageDataArray[pos + 2] = fillColor.b;
        imageDataArray[pos + 3] = fillColor.a;
        
        pixelsToCheck.push({ x: x + 1, y });
        pixelsToCheck.push({ x: x - 1, y });
        pixelsToCheck.push({ x, y: y + 1 });
        pixelsToCheck.push({ x, y: y - 1 });
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }, []);

  // Función para aplicar corte remoto de selección
  const applyRemoteCut = useCallback((data) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(data.x, data.y, data.width, data.height);
    saveToHistory(); // Guardar en historial después del cambio remoto
  }, [saveToHistory]);

  // Función para aplicar selección remota
  const applyRemoteSelection = useCallback((data) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Crear imagen desde data URL
    const img = new Image();
    img.onload = () => {
      // Dibujar la imagen en la nueva posición y tamaño
      ctx.drawImage(
        img, 
        0, 0, data.originalWidth, data.originalHeight,
        data.area.x, data.area.y, data.area.width, data.area.height
      );
      saveToHistory(); // Guardar en historial después del cambio remoto
    };
    img.src = data.imageDataURL;
  }, [saveToHistory]);

  // Función para dibujar trazos de otros usuarios
  const drawRemoteStroke = useCallback((data) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.save();
    
    // Configuración para mejor calidad
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.globalCompositeOperation = data.tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.globalAlpha = data.opacity;
    ctx.fillStyle = data.color;  // Agregar fillStyle para texturas
    
    // Aplicar el mismo efecto que el usuario original
    if (data.brushType === 'spray') {
      // Efecto spray/aerosol
      drawSpray(ctx, data.currentPos, data.size, data.color);
    } else if (data.brushType === 'blur') {
      // Efecto difuminado
      ctx.filter = `blur(${Math.max(1, data.size / 5)}px)`;
      ctx.strokeStyle = data.color;
      ctx.lineWidth = data.size * 1.5;
      ctx.beginPath();
      ctx.moveTo(data.lastPos.x, data.lastPos.y);
      ctx.lineTo(data.currentPos.x, data.currentPos.y);
      ctx.stroke();
      ctx.filter = 'none';
    } else if (data.brushType === 'graffiti') {
      // Efecto grafiti con textura irregular
      drawGraffiti(ctx, data.lastPos, data.currentPos, data.size, data.color);
    } else if (data.brushType === 'texture') {
      // Pincel con textura
      drawTextured(ctx, data.lastPos, data.currentPos, data.size, data.color);
    } else {
      // Pincel normal - con interpolación para movimientos rápidos
      ctx.strokeStyle = data.color;
      ctx.lineWidth = data.size;
      
      // Calcular distancia para interpolación
      const distance = Math.sqrt(
        Math.pow(data.currentPos.x - data.lastPos.x, 2) + 
        Math.pow(data.currentPos.y - data.lastPos.y, 2)
      );
      
      // Si la distancia es grande (movimiento rápido), interpolar puntos
      if (distance > data.size * 2) {
        const steps = Math.ceil(distance / (data.size / 2));
        ctx.beginPath();
        ctx.moveTo(data.lastPos.x, data.lastPos.y);
        
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const x = data.lastPos.x + (data.currentPos.x - data.lastPos.x) * t;
          const y = data.lastPos.y + (data.currentPos.y - data.lastPos.y) * t;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      } else {
        // Para distancias cortas, línea directa
        ctx.beginPath();
        ctx.moveTo(data.lastPos.x, data.lastPos.y);
        ctx.lineTo(data.currentPos.x, data.currentPos.y);
        ctx.stroke();
      }
    }
    
    ctx.restore();
  }, []);

  // Función para dibujar formas de otros usuarios
  const drawRemoteShape = useCallback((data) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.save();
    
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.globalCompositeOperation = data.tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.globalAlpha = data.opacity;
    ctx.strokeStyle = data.color;
    ctx.lineWidth = data.size;
    
    if (data.shapeType === 'line') {
      ctx.beginPath();
      ctx.moveTo(data.startPos.x, data.startPos.y);
      ctx.lineTo(data.endPos.x, data.endPos.y);
      ctx.stroke();
    } else if (data.shapeType === 'circle') {
      const radius = Math.sqrt(Math.pow(data.endPos.x - data.startPos.x, 2) + Math.pow(data.endPos.y - data.startPos.y, 2));
      ctx.beginPath();
      ctx.arc(data.startPos.x, data.startPos.y, radius, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (data.shapeType === 'rectangle') {
      const width = data.endPos.x - data.startPos.x;
      const height = data.endPos.y - data.startPos.y;
      ctx.beginPath();
      ctx.rect(data.startPos.x, data.startPos.y, width, height);
      ctx.stroke();
    }
    
    ctx.restore();
  }, []);

  // Inicializar Socket.IO después de que drawRemoteStroke esté definida
  useEffect(() => {
    // Inicializar Socket.IO
    const socketConfig = getSocketUrl();
    if (typeof socketConfig === 'string') {
      // Desarrollo local
      socketRef.current = io(socketConfig);
    } else {
      // Producción con path personalizado
      socketRef.current = io(socketConfig.url, { path: socketConfig.path });
    }
    console.log('🔌 Conectando a Socket.IO en:', socketConfig);
    
    // Escuchar eventos de dibujo en tiempo real
    socketRef.current.on('drawing', (data) => {
      setTimeout(() => drawRemoteStroke(data), 100); // Delay de 100ms para suavidad
    });
    
    // Escuchar eventos de formas
    socketRef.current.on('shape', (data) => {
      setTimeout(() => drawRemoteShape(data), 100);
    });
    
    // Escuchar eventos de bucket fill
    socketRef.current.on('bucket-fill', (data) => {
      setTimeout(() => applyRemoteBucketFill(data), 100);
    });
    
    // Escuchar eventos de undo
    socketRef.current.on('undo', (data) => {
      const canvas = canvasRef.current;
      if (canvas && data.canvasData) {
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = data.canvasData;
      }
    });
    
    // Escuchar eventos de redo
    socketRef.current.on('redo', (data) => {
      const canvas = canvasRef.current;
      if (canvas && data.canvasData) {
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = data.canvasData;
      }
    });
    
    // Escuchar eventos de corte de selección
    socketRef.current.on('cut-selection', (data) => {
      setTimeout(() => applyRemoteCut(data), 10); // Reducir delay para mejor sincronización
    });
    
    // Escuchar eventos de aplicar selección
    socketRef.current.on('apply-selection', (data) => {
      setTimeout(() => applyRemoteSelection(data), 10); // Reducir delay para mejor sincronización
    });
    
    socketRef.current.on('clearBoard', () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    });
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [drawRemoteStroke, drawRemoteShape, applyRemoteBucketFill, applyRemoteCut, applyRemoteSelection]);

  // Dibujar selección en el canvas de preview
  useEffect(() => {
    const previewCanvas = previewCanvasRef.current;
    if (!previewCanvas) return;
    
    const ctx = previewCanvas.getContext('2d');
    ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    
    // Dibujar rectángulo de selección en progreso
    if (isSelecting && selectionStart && selectionEnd) {
      const x = Math.min(selectionStart.x, selectionEnd.x);
      const y = Math.min(selectionStart.y, selectionEnd.y);
      const width = Math.abs(selectionEnd.x - selectionStart.x);
      const height = Math.abs(selectionEnd.y - selectionStart.y);
      
      ctx.strokeStyle = '#0066ff';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(x, y, width, height);
      ctx.setLineDash([]);
    }
    
    // Dibujar área seleccionada con la imagen recortada
    if (selectedArea) {
      // Dibujar la imagen recortada si existe
      if (selectedImageData) {
        // Crear canvas temporal para la imagen
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = selectedImageData.width;
        tempCanvas.height = selectedImageData.height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.putImageData(selectedImageData, 0, 0);
        
        // Dibujar la imagen redimensionada en la posición actual
        ctx.drawImage(
          tempCanvas, 
          0, 0, selectedImageData.width, selectedImageData.height,
          selectedArea.x, selectedArea.y, selectedArea.width, selectedArea.height
        );
      }
      
      // Dibujar borde de selección
      ctx.strokeStyle = '#0066ff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(selectedArea.x, selectedArea.y, selectedArea.width, selectedArea.height);
      ctx.setLineDash([]);
      
      // Dibujar handles de redimensionamiento
      const handleSize = 8;
      ctx.fillStyle = '#0066ff';
      
      // Esquina superior izquierda
      ctx.fillRect(selectedArea.x - handleSize/2, selectedArea.y - handleSize/2, handleSize, handleSize);
      // Esquina superior derecha
      ctx.fillRect(selectedArea.x + selectedArea.width - handleSize/2, selectedArea.y - handleSize/2, handleSize, handleSize);
      // Esquina inferior izquierda
      ctx.fillRect(selectedArea.x - handleSize/2, selectedArea.y + selectedArea.height - handleSize/2, handleSize, handleSize);
      // Esquina inferior derecha
      ctx.fillRect(selectedArea.x + selectedArea.width - handleSize/2, selectedArea.y + selectedArea.height - handleSize/2, handleSize, handleSize);
    }
  }, [isSelecting, selectionStart, selectionEnd, selectedArea, selectedImageData]);

  // Balde de relleno
  const floodFill = useCallback((startX, startY) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    const startPos = (Math.floor(startY) * canvas.width + Math.floor(startX)) * 4;
    const startR = data[startPos];
    const startG = data[startPos + 1];
    const startB = data[startPos + 2];
    const startA = data[startPos + 3];
    
    const fillColor = {
      r: parseInt(currentColor.slice(1, 3), 16),
      g: parseInt(currentColor.slice(3, 5), 16),
      b: parseInt(currentColor.slice(5, 7), 16),
      a: Math.floor(255 * opacity / 100)
    };
    
    if (startR === fillColor.r && startG === fillColor.g && startB === fillColor.b) return;
    
    const pixelsToCheck = [{ x: Math.floor(startX), y: Math.floor(startY) }];
    
    while (pixelsToCheck.length > 0) {
      const { x, y } = pixelsToCheck.pop();
      
      if (x < 0 || x >= canvas.width || y < 0 || y >= canvas.height) continue;
      
      const pos = (y * canvas.width + x) * 4;
      
      if (data[pos] === startR && data[pos + 1] === startG && 
          data[pos + 2] === startB && data[pos + 3] === startA) {
        
        data[pos] = fillColor.r;
        data[pos + 1] = fillColor.g;
        data[pos + 2] = fillColor.b;
        data[pos + 3] = fillColor.a;
        
        pixelsToCheck.push({ x: x + 1, y });
        pixelsToCheck.push({ x: x - 1, y });
        pixelsToCheck.push({ x, y: y + 1 });
        pixelsToCheck.push({ x, y: y - 1 });
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    // Emitir evento de bucket fill a otros usuarios
    if (socketRef.current) {
      socketRef.current.emit('bucket-fill', {
        x: startX,
        y: startY,
        color: currentColor,
        opacity: opacity
      });
    }
    
    saveToHistory();
    // Guardar inmediatamente al servidor
    setTimeout(() => saveDrawingToServer(), 100);
  }, [currentColor, opacity, saveToHistory]);

  // Gotero
  const pickColor = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1);
    const data = imageData.data;
    
    const hex = `#${((1 << 24) + (data[0] << 16) + (data[1] << 8) + data[2]).toString(16).slice(1)}`;
    setCurrentColor(hex);
    setCurrentTool('brush');
  }, []);

  // Dibujar formas
  const drawShape = useCallback((start, end) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = currentColor;
    ctx.lineWidth = brushSize;
    ctx.globalAlpha = (opacity - transparency) / 100;
    
    ctx.beginPath();
    
    if (currentTool === 'line') {
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
    } else if (currentTool === 'circle') {
      const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
      ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
    } else if (currentTool === 'rectangle') {
      ctx.rect(start.x, start.y, end.x - start.x, end.y - start.y);
    }
    
    ctx.stroke();
    ctx.globalAlpha = 1;
    saveToHistory();
  }, [currentTool, currentColor, brushSize, opacity, saveToHistory]);

  // Dibujar previsualización de formas
  const drawPreview = useCallback((start, end) => {
    const previewCanvas = previewCanvasRef.current;
    if (!previewCanvas) return;

    const ctx = previewCanvas.getContext('2d');
    
    // Limpiar canvas de preview
    ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    
    ctx.save();
    ctx.strokeStyle = currentColor;
    ctx.lineWidth = brushSize;
    ctx.globalAlpha = 0.7;
    ctx.setLineDash([5, 5]); // Línea punteada para preview
    
    ctx.beginPath();
    
    if (currentTool === 'line') {
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
    } else if (currentTool === 'circle') {
      const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
      ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
    } else if (currentTool === 'rectangle') {
      ctx.rect(start.x, start.y, end.x - start.x, end.y - start.y);
    }
    
    ctx.stroke();
    ctx.restore();
  }, [currentTool, currentColor, brushSize]);

  // Manejadores de eventos del mouse/touch
  const handleStart = useCallback((e) => {
    e.preventDefault();
    const pos = getCanvasCoordinates(e);
    
    if (currentTool === 'bucket') {
      floodFill(pos.x, pos.y);
      return;
    }
    
    if (currentTool === 'eyedropper') {
      pickColor(pos.x, pos.y);
      return;
    }
    
    if (currentTool === 'line' || currentTool === 'circle' || currentTool === 'rectangle') {
      if (!isDrawingShape) {
        setShapeStart(pos);
        setIsDrawingShape(true);
      } else {
        drawShape(shapeStart, pos);
        setIsDrawingShape(false);
        setShapeStart(null);
      }
      return;
    }
    
    if (currentTool === 'select') {
      // Si hay una selección activa, verificar dónde se hace clic
      if (selectedArea) {
        const handle = getResizeHandle(pos);
        
        if (handle) {
          // Iniciar redimensionamiento
          setIsResizing(true);
          setResizeHandle(handle);
          setLastPos(pos);
        } else if (
          pos.x >= selectedArea.x && pos.x <= selectedArea.x + selectedArea.width &&
          pos.y >= selectedArea.y && pos.y <= selectedArea.y + selectedArea.height
        ) {
          // Iniciar movimiento de la selección
          setIsDraggingSelection(true);
          setLastPos(pos);
        } else {
          // Aplicar selección actual y crear nueva
          applySelection();
          setIsSelecting(true);
          setSelectionStart(pos);
        }
      } else {
        // Iniciar nueva selección
        setIsSelecting(true);
        setSelectionStart(pos);
      }
      return;
    }

    setIsDrawing(true);
    setLastPos(pos);
  }, [currentTool, getCanvasCoordinates, floodFill, pickColor, drawShape, isDrawingShape, shapeStart, selectedArea, getResizeHandle, applySelection]);

  const handleMove = useCallback((e) => {
    e.preventDefault();
    
    const pos = getCanvasCoordinates(e);
    
    // Manejar selección
    if (isSelecting) {
      setSelectionEnd(pos);
      return;
    }
    
    // Manejar redimensionamiento de selección
    if (isResizing && selectedArea && resizeHandle) {
      const deltaX = pos.x - lastPos.x;
      const deltaY = pos.y - lastPos.y;
      
      setSelectedArea(prev => {
        const newArea = { ...prev };
        
        switch (resizeHandle) {
          case 'top-left':
            newArea.x += deltaX;
            newArea.y += deltaY;
            newArea.width -= deltaX;
            newArea.height -= deltaY;
            break;
          case 'top-right':
            newArea.y += deltaY;
            newArea.width += deltaX;
            newArea.height -= deltaY;
            break;
          case 'bottom-left':
            newArea.x += deltaX;
            newArea.width -= deltaX;
            newArea.height += deltaY;
            break;
          case 'bottom-right':
            newArea.width += deltaX;
            newArea.height += deltaY;
            break;
        }
        
        // Evitar tamaños negativos
        if (newArea.width < 10) newArea.width = 10;
        if (newArea.height < 10) newArea.height = 10;
        
        return newArea;
      });
      
      setLastPos(pos);
      return;
    }
    
    // Manejar movimiento de selección
    if (isDraggingSelection && selectedArea) {
      const deltaX = pos.x - lastPos.x;
      const deltaY = pos.y - lastPos.y;
      
      setSelectedArea(prev => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastPos(pos);
      return;
    }

    // Mostrar preview para formas
    if (isDrawingShape && shapeStart && (currentTool === 'line' || currentTool === 'circle' || currentTool === 'rectangle')) {
      drawPreview(shapeStart, pos);
      return;
    }

    if (!isDrawing) return;    // Dibujar directamente para todas las herramientas
    if (currentTool !== 'bucket' && currentTool !== 'eyedropper') {
      const distance = Math.sqrt(
        Math.pow(pos.x - lastPos.x, 2) + 
        Math.pow(pos.y - lastPos.y, 2)
      );
      
      // Reducir la sensibilidad para capturar más puntos en movimientos rápidos
      if (distance > 0.1) {
        draw(lastPos, pos);
        setLastPos(pos);
      }
    }
    
    // Solo guardar ocasionalmente para no saturar el servidor
    // El guardado automático se hace cada 10 segundos
  }, [isSelecting, isResizing, isDraggingSelection, isDrawingShape, shapeStart, currentTool, lastPos, getCanvasCoordinates, draw, drawPreview, selectedArea, resizeHandle]);

  const handleEnd = useCallback((e) => {
    e.preventDefault();
    
    // Finalizar selección
    if (isSelecting) {
      setIsSelecting(false);
      const pos = getCanvasCoordinates(e);
      
      if (selectionStart && pos) {
        const x = Math.min(selectionStart.x, pos.x);
        const y = Math.min(selectionStart.y, pos.y);
        const width = Math.abs(pos.x - selectionStart.x);
        const height = Math.abs(pos.y - selectionStart.y);
        
        if (width > 10 && height > 10) { // Mínimo tamaño de selección
          setSelectedArea({ x, y, width, height });
          // Recortar automáticamente el área seleccionada
          setTimeout(() => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(x, y, width, height);
            setSelectedImageData(imageData);
            
            // Limpiar el área del canvas original
            ctx.clearRect(x, y, width, height);
            
            // Emitir evento de corte a otros usuarios
            if (socketRef.current) {
              socketRef.current.emit('cut-selection', {
                x, y, width, height
              });
            }
            
            saveToHistory();
          }, 10); // Reducir delay para mejor sincronización
        }
      }
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }
    
    // Finalizar redimensionamiento
    if (isResizing) {
      setIsResizing(false);
      setResizeHandle(null);
      return;
    }
    
    // Finalizar movimiento de selección
    if (isDraggingSelection) {
      setIsDraggingSelection(false);
      return;
    }

    // Manejar formas cuando se completa el dibujo
    if (isDrawingShape && shapeStart) {
      const pos = getCanvasCoordinates(e);
      const canvas = canvasRef.current;
      
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.globalCompositeOperation = currentTool === 'eraser' ? 'destination-out' : 'source-over';
        ctx.globalAlpha = (opacity - transparency) / 100;
        ctx.strokeStyle = currentColor;
        ctx.lineWidth = brushSize;
        
        // Dibujar la forma final en el canvas principal
        if (currentTool === 'line') {
          ctx.beginPath();
          ctx.moveTo(shapeStart.x, shapeStart.y);
          ctx.lineTo(pos.x, pos.y);
          ctx.stroke();
        } else if (currentTool === 'circle') {
          const radius = Math.sqrt(Math.pow(pos.x - shapeStart.x, 2) + Math.pow(pos.y - shapeStart.y, 2));
          ctx.beginPath();
          ctx.arc(shapeStart.x, shapeStart.y, radius, 0, 2 * Math.PI);
          ctx.stroke();
        } else if (currentTool === 'rectangle') {
          const width = pos.x - shapeStart.x;
          const height = pos.y - shapeStart.y;
          ctx.beginPath();
          ctx.rect(shapeStart.x, shapeStart.y, width, height);
          ctx.stroke();
        }
        
        ctx.restore();
        
        // Emitir forma a otros usuarios
        if (socketRef.current) {
          socketRef.current.emit('shape', {
            shapeType: currentTool,
            startPos: shapeStart,
            endPos: pos,
            tool: currentTool,
            color: currentColor,
            size: brushSize,
            opacity: opacity / 100
          });
        }
        
        saveToHistory();
        setTimeout(() => saveDrawingToServer(), 1000);
      }
      
      // Limpiar preview
      const previewCanvas = previewCanvasRef.current;
      if (previewCanvas) {
        const ctx = previewCanvas.getContext('2d');
        ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      }
      
      setIsDrawingShape(false);
      setShapeStart(null);
      return;
    }
    
    if (isDrawing) {
      setIsDrawing(false);
      setPoints([]); // Limpiar puntos
      saveToHistory();
      // Guardar al servidor al completar un trazo
      setTimeout(() => saveDrawingToServer(), 1000);
    }
  }, [isSelecting, isResizing, isDraggingSelection, isDrawing, isDrawingShape, shapeStart, saveToHistory, getCanvasCoordinates, currentTool, currentColor, brushSize, opacity, selectedArea, applySelection]);

  // Manejar click de rueda del mouse para pan
  const handleMouseDown = useCallback((e) => {
    if (e.button === 1) { // Rueda del mouse (botón medio)
      e.preventDefault();
      setIsMiddleMousePanning(true);
      setLastPos({ x: e.clientX, y: e.clientY });
      return;
    }
    handleStart(e);
  }, [handleStart]);

  const handleMouseMove = useCallback((e) => {
    if (isMiddleMousePanning) {
      e.preventDefault();
      setPan(prev => ({
        x: prev.x + (e.clientX - lastPos.x),
        y: prev.y + (e.clientY - lastPos.y)
      }));
      setLastPos({ x: e.clientX, y: e.clientY });
      return;
    }
    handleMove(e);
  }, [isMiddleMousePanning, lastPos, handleMove]);

  const handleMouseUp = useCallback((e) => {
    if (e.button === 1) { // Rueda del mouse
      e.preventDefault();
      setIsMiddleMousePanning(false);
      return;
    }
    handleEnd(e);
  }, [handleEnd]);

  // Prevenir el zoom del navegador
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Manejo de gestos táctiles para zoom/rotación (móvil)
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastTouchAngle, setLastTouchAngle] = useState(0);

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchAngle = (touches) => {
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.atan2(dy, dx) * 180 / Math.PI;
  };

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      setLastTouchDistance(getTouchDistance(e.touches));
      setLastTouchAngle(getTouchAngle(e.touches));
    } else {
      handleStart(e);
    }
  }, [handleStart]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      const distance = getTouchDistance(e.touches);
      const angle = getTouchAngle(e.touches);
      
      if (lastTouchDistance > 0) {
        const zoomFactor = distance / lastTouchDistance;
        setZoom(prev => Math.max(0.1, Math.min(5, prev * zoomFactor)));
      }
      
      if (Math.abs(lastTouchAngle - angle) < 90) {
        setRotation(prev => prev + (angle - lastTouchAngle));
      }
      
      setLastTouchDistance(distance);
      setLastTouchAngle(angle);
    } else {
      handleMove(e);
    }
  }, [lastTouchDistance, lastTouchAngle, handleMove]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      setLastTouchDistance(0);
    }
    handleEnd(e);
  }, [handleEnd]);

  // Configurar eventos táctiles con { passive: false }
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // Registrar eventos touch con configuración no-pasiva
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // Registrar evento wheel con configuración no-pasiva
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleWheel]);

  // Funciones de control
  const undo = () => {
    if (historyStep > 0) {
      setHistoryStep(prev => prev - 1);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        // Guardar el estado después del undo
        saveDrawingToServer();
        
        // Emitir undo a otros usuarios
        if (socketRef.current) {
          socketRef.current.emit('undo', {
            canvasData: canvas.toDataURL()
          });
        }
      };
      img.src = history[historyStep - 1];
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      setHistoryStep(prev => prev + 1);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        // Guardar el estado después del redo
        saveDrawingToServer();
        
        // Emitir redo a otros usuarios
        if (socketRef.current) {
          socketRef.current.emit('redo', {
            canvasData: canvas.toDataURL()
          });
        }
      };
      img.src = history[historyStep + 1];
    }
  };

  // Manejar teclas para la selección y atajos globales
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Atajos globales
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      
      if (e.ctrlKey && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      
      // Teclas específicas para selección
      if (currentTool === 'select' && selectedArea) {
        if (e.key === 'Enter') {
          // Confirmar selección
          applySelection();
        } else if (e.key === 'Escape') {
          // Cancelar selección - devolver imagen al lugar original
          if (selectedImageData) {
            const canvas = canvasRef.current;
            if (canvas) {
              const ctx = canvas.getContext('2d');
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = selectedImageData.width;
              tempCanvas.height = selectedImageData.height;
              const tempCtx = tempCanvas.getContext('2d');
              tempCtx.putImageData(selectedImageData, 0, 0);
              
              // Restaurar en la posición original
              ctx.drawImage(tempCanvas, selectedArea.x, selectedArea.y);
            }
          }
          setSelectedArea(null);
          setSelectedImageData(null);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTool, selectedArea, selectedImageData, applySelection]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    

    if (socketRef.current) {
      socketRef.current.emit('clearBoard');
    }
    
    saveToHistory();
    setTimeout(() => saveDrawingToServer(), 1000);
  };

  const saveImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `pizarra-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);
  };

  const rotateCanvas = (degrees) => {
    setRotation(prev => prev + degrees);
  };

  // Colores predefinidos
  const presetColors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#FFC0CB', '#A52A2A',
    '#8a2be2', '#4b0082', '#9932cc', '#dda0dd', '#1a0d2e', '#2d1b69'
  ];

  return (
    <div className="simple-drawing-board">
      {/* Barra lateral de herramientas */}
      <div className="sidebar">
        <div className="sidebar-header">
          <button onClick={onBack} className="back-button">
            <i className="fas fa-arrow-left"></i>
          </button>
          <h3>Pizarrón</h3>
        </div>

        {/* Herramientas */}
        <div className="tool-section">
          <h4>Herramientas</h4>
          <div className="tool-grid">
            <button className={`tool-btn ${currentTool === 'brush' ? 'active' : ''}`} 
                    onClick={() => setCurrentTool('brush')} title="Pincel">
              <i className="fas fa-paint-brush"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'eraser' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('eraser')} title="Borrador">
              <i className="fas fa-eraser"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'line' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('line')} title="Línea">
              <i className="fas fa-minus"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'circle' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('circle')} title="Círculo">
              <i className="far fa-circle"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'rectangle' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('rectangle')} title="Rectángulo">
              <i className="far fa-square"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'bucket' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('bucket')} title="Balde">
              <i className="fas fa-fill-drip"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'eyedropper' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('eyedropper')} title="Gotero">
              <i className="fas fa-eye-dropper"></i>
            </button>
            <button className={`tool-btn ${currentTool === 'select' ? 'active' : ''}`}
                    onClick={() => setCurrentTool('select')} title="Seleccionar">
              <i className="fas fa-expand-arrows-alt"></i>
            </button>
          </div>
        </div>

        {/* Colores */}
        <div className="tool-section">
          <h4>Colores</h4>
          <input type="color" value={currentColor} onChange={(e) => setCurrentColor(e.target.value)} 
                 className="color-picker" />
          <div className="color-grid">
            {presetColors.map(color => (
              <button key={color} className={`color-btn ${currentColor === color ? 'active' : ''}`}
                      style={{backgroundColor: color}} onClick={() => setCurrentColor(color)} />
            ))}
          </div>
        </div>

        {/* Propiedades del pincel */}
        <div className="tool-section">
          <h4>Pincel</h4>
          
          {/* Tipos de pincel */}
          <label>Tipo de pincel:</label>
          <div className="brush-type-grid">
            <button className={`brush-type-btn ${brushType === 'normal' ? 'active' : ''}`}
                    onClick={() => setBrushType('normal')} title="Normal">
              <i className="fas fa-paint-brush"></i>
            </button>
            <button className={`brush-type-btn ${brushType === 'spray' ? 'active' : ''}`}
                    onClick={() => setBrushType('spray')} title="Aerosol">
              <i className="fas fa-spray-can"></i>
            </button>
            <button className={`brush-type-btn ${brushType === 'blur' ? 'active' : ''}`}
                    onClick={() => setBrushType('blur')} title="Difuminado">
              <i className="fas fa-cloud"></i>
            </button>
            <button className={`brush-type-btn ${brushType === 'graffiti' ? 'active' : ''}`}
                    onClick={() => setBrushType('graffiti')} title="Grafiti">
              <i className="fas fa-hashtag"></i>
            </button>
            <button className={`brush-type-btn ${brushType === 'texture' ? 'active' : ''}`}
                    onClick={() => setBrushType('texture')} title="Textura">
              <i className="fas fa-th"></i>
            </button>
          </div>
          
          <label>Tamaño: {brushSize}px</label>
          <input type="range" min="1" max="50" value={brushSize} 
                 onChange={(e) => setBrushSize(parseInt(e.target.value))} className="slider" />
          
          <label>Opacidad: {opacity}%</label>
          <input type="range" min="1" max="100" value={opacity} 
                 onChange={(e) => setOpacity(parseInt(e.target.value))} className="slider" />
        </div>

        {/* Acciones */}
        <div className="tool-section">
          <h4>Acciones</h4>
          <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
            <button onClick={undo} className="action-btn" disabled={historyStep <= 0} style={{ flex: 1 }}>
              <i className="fas fa-undo"></i> Deshacer
            </button>
            <button onClick={redo} className="action-btn" disabled={historyStep >= history.length - 1} style={{ flex: 1 }}>
              <i className="fas fa-redo"></i> Rehacer
            </button>
          </div>
          <button onClick={clearCanvas} className="action-btn">
            <i className="fas fa-trash"></i> Limpiar
          </button>
          <button onClick={saveImage} className="action-btn">
            <i className="fas fa-download"></i> Guardar
          </button>
          <button onClick={resetView} className="action-btn">
            <i className="fas fa-expand-arrows-alt"></i> Reset Vista
          </button>
        </div>

        {/* Controles de rotación (solo desktop) */}
        {!isMobile && (
          <div className="tool-section">
            <h4>Rotación</h4>
            <button onClick={() => rotateCanvas(-90)} className="action-btn">
              <i className="fas fa-undo"></i> 90° ←
            </button>
            <button onClick={() => rotateCanvas(90)} className="action-btn">
              <i className="fas fa-redo"></i> 90° →
            </button>
          </div>
        )}
      </div>

      {/* Área del canvas */}
      <div className="canvas-area" ref={containerRef}>
        <div className="canvas-wrapper">
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="drawing-canvas"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: 'center center'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          />
          <canvas
            ref={previewCanvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="preview-canvas"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: 'center center'
            }}
          />
        </div>
        
        {/* Indicadores de zoom y rotación */}
        <div className="view-info">
          Zoom: {Math.round(zoom * 100)}% | Rotación: {Math.round(rotation)}°
        </div>
      </div>
    </div>
  );
};

export default SimpleDrawingBoard;
